/**
 * AdContext - Strategic Ad Placement System
 * 
 * Provides context-aware ad placement that respects user experience
 * and complies with Google Play policies.
 * 
 * POLICY COMPLIANCE:
 * - Ads never appear inside notifications
 * - Ads never interrupt critical flows (logging water, saving data)
 * - Ads never mimic system messages
 * - Ads never appear as part of health/medical advice
 * - Ad frequency is NOT increased as response to ignored notifications
 * 
 * STRATEGIC PLACEMENTS (approved):
 * - App open after notification return (below recovery message)
 * - Session transition moments (after logging, goal completion)
 * - Bottom persistent banner (non-intrusive)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePremiumStatus } from "@/hooks/usePremiumStatus";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { isAndroidApp } from "@/utils/platform";

interface AdContextType {
  // State
  canShowAds: boolean;
  isAdFree: boolean;
  
  // Session tracking for strategic placement
  sessionAdCount: number;
  lastAdShownAt: number | null;
  
  // Placement approval functions
  canShowBannerAd: () => boolean;
  canShowTransitionAd: () => boolean;
  canShowRecoveryAd: () => boolean;
  
  // Recording functions
  recordAdShown: (placementType: AdPlacementType) => void;
  recordAdClicked: (placementType: AdPlacementType) => void;
  
  // Critical flow protection
  enterCriticalFlow: () => void;
  exitCriticalFlow: () => void;
  isInCriticalFlow: boolean;
}

export type AdPlacementType = 
  | 'banner_main'
  | 'banner_stats'
  | 'banner_achievements'
  | 'transition_drink_logged'
  | 'transition_goal_complete'
  | 'transition_section_change'
  | 'recovery_return';

// Configuration constants
const AD_CONFIG = {
  // Maximum ads per session
  MAX_SESSION_ADS: 6,
  // Minimum seconds between transition ads
  MIN_TRANSITION_INTERVAL_SECONDS: 180, // 3 minutes
  // Minimum seconds between recovery ads
  MIN_RECOVERY_INTERVAL_SECONDS: 3600, // 1 hour
  // Cool down after critical flow (seconds)
  CRITICAL_FLOW_COOLDOWN_SECONDS: 5,
};

const AdContext = createContext<AdContextType | undefined>(undefined);

interface AdProviderProps {
  children: ReactNode;
}

export const AdProvider = ({ children }: AdProviderProps) => {
  const { user } = useAuth();
  const { isPaidPremium } = usePremiumStatus();
  const { isEnabled } = useFeatureFlags();
  const isAdsEnabled = isEnabled('adsEnabled');
  
  const [sessionAdCount, setSessionAdCount] = useState(0);
  const [lastAdShownAt, setLastAdShownAt] = useState<number | null>(null);
  const [isInCriticalFlow, setIsInCriticalFlow] = useState(false);
  const [criticalFlowEndedAt, setCriticalFlowEndedAt] = useState<number | null>(null);

  // Check if user has been active for at least 2 days (retention protection)
  const hasMinUsageDays = (() => {
    const firstUse = localStorage.getItem('hydrolife_first_use');
    if (!firstUse) {
      localStorage.setItem('hydrolife_first_use', Date.now().toString());
      return false;
    }
    const daysSinceFirst = (Date.now() - parseInt(firstUse, 10)) / (1000 * 60 * 60 * 24);
    return daysSinceFirst >= 2;
  })();

  // Determine if ads can be shown at all
  // Android app users see ads even though they get auto-premium status
  const inApp = isAndroidApp();
  const canShowAds = Boolean(
    user && // User must be authenticated
    (inApp || !isPaidPremium) && // In-app users always see ads; web premium users don't
    isAdsEnabled && // Ads feature flag is enabled
    hasMinUsageDays // User has used app for at least 2 days
  );

  const isAdFree = isPaidPremium && !inApp;

  /**
   * Check if enough time has passed since last ad
   */
  const hasMinIntervalPassed = useCallback((minSeconds: number): boolean => {
    if (!lastAdShownAt) return true;
    const elapsed = (Date.now() - lastAdShownAt) / 1000;
    return elapsed >= minSeconds;
  }, [lastAdShownAt]);

  /**
   * Check if we're within session ad limit
   */
  const isWithinSessionLimit = useCallback((): boolean => {
    return sessionAdCount < AD_CONFIG.MAX_SESSION_ADS;
  }, [sessionAdCount]);

  /**
   * Check if critical flow cooldown has passed
   */
  const hasCriticalCooldownPassed = useCallback((): boolean => {
    if (isInCriticalFlow) return false;
    if (!criticalFlowEndedAt) return true;
    const elapsed = (Date.now() - criticalFlowEndedAt) / 1000;
    return elapsed >= AD_CONFIG.CRITICAL_FLOW_COOLDOWN_SECONDS;
  }, [isInCriticalFlow, criticalFlowEndedAt]);

  /**
   * Can show a banner ad (always visible on non-critical screens)
   */
  const canShowBannerAd = useCallback((): boolean => {
    return canShowAds && !isInCriticalFlow;
  }, [canShowAds, isInCriticalFlow]);

  /**
   * Can show a transition ad (after actions like logging water)
   */
  const canShowTransitionAd = useCallback((): boolean => {
    return (
      canShowAds &&
      isWithinSessionLimit() &&
      hasCriticalCooldownPassed() &&
      hasMinIntervalPassed(AD_CONFIG.MIN_TRANSITION_INTERVAL_SECONDS)
    );
  }, [canShowAds, isWithinSessionLimit, hasCriticalCooldownPassed, hasMinIntervalPassed]);

  /**
   * Can show a recovery ad (when user returns after ignoring notifications)
   */
  const canShowRecoveryAd = useCallback((): boolean => {
    return (
      canShowAds &&
      isWithinSessionLimit() &&
      hasCriticalCooldownPassed() &&
      hasMinIntervalPassed(AD_CONFIG.MIN_RECOVERY_INTERVAL_SECONDS)
    );
  }, [canShowAds, isWithinSessionLimit, hasCriticalCooldownPassed, hasMinIntervalPassed]);

  /**
   * Record that an ad was shown
   */
  const recordAdShown = useCallback((placementType: AdPlacementType) => {
    setSessionAdCount(prev => prev + 1);
    setLastAdShownAt(Date.now());
    
    // Analytics could be added here
    console.log(`[Ad] Shown: ${placementType}`);
  }, []);

  /**
   * Record that an ad was clicked
   */
  const recordAdClicked = useCallback((placementType: AdPlacementType) => {
    // Analytics could be added here
    console.log(`[Ad] Clicked: ${placementType}`);
  }, []);

  /**
   * Enter a critical flow (e.g., logging water, saving settings)
   * Ads will not be shown during this time
   */
  const enterCriticalFlow = useCallback(() => {
    setIsInCriticalFlow(true);
  }, []);

  /**
   * Exit a critical flow
   */
  const exitCriticalFlow = useCallback(() => {
    setIsInCriticalFlow(false);
    setCriticalFlowEndedAt(Date.now());
  }, []);

  // Reset session count on new session (page reload or after 30 min inactivity)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if it's been more than 30 minutes
        const lastActivity = localStorage.getItem('lastAdActivity');
        if (lastActivity) {
          const elapsed = Date.now() - parseInt(lastActivity, 10);
          if (elapsed > 30 * 60 * 1000) {
            setSessionAdCount(0);
          }
        }
      }
      localStorage.setItem('lastAdActivity', Date.now().toString());
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const value: AdContextType = {
    canShowAds,
    isAdFree,
    sessionAdCount,
    lastAdShownAt,
    canShowBannerAd,
    canShowTransitionAd,
    canShowRecoveryAd,
    recordAdShown,
    recordAdClicked,
    enterCriticalFlow,
    exitCriticalFlow,
    isInCriticalFlow,
  };

  return <AdContext.Provider value={value}>{children}</AdContext.Provider>;
};

export const useAds = (): AdContextType => {
  const context = useContext(AdContext);
  if (context === undefined) {
    throw new Error('useAds must be used within an AdProvider');
  }
  return context;
};

export default AdContext;
